import { Loader2, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";

// One pack from /api/web/packs (shared/config/packs.js publicPack), as a card
// with its two prices, its credit count and one Buy button. Used by /pricing
// and by the landing page's credits section.

export interface Pack {
  type: string;
  kind: "video" | "credits";
  points: number;
  credits: number;
  price_cents: number; // centavos MXN — what Stripe charges
  usd_cents: number;   // display anchor, never charged
  currency: string;
  name: string;
  description: string;
}

// CURRENCY RULE (user-mandated): every amount in code and everything sent to
// Stripe is MXN — the account clears through a Mexican bank and non-MXN
// amounts get Mexican customers' cards declined. Stripe's adaptive pricing
// converts for foreign buyers automatically. The USD figure is the pack's
// declared display anchor (shared/config/packs.js usd_cents), shown at the
// same weight as the MXN amount so nobody has to guess which currency a bare
// "$1,799" is — and so a US reader sees the number they will actually pay.
const mxn = (cents: number) =>
  `$${Math.round(cents / 100).toLocaleString("en-US")}`;

const usd = (cents: number) => {
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/** MXN per credit, two decimals — the number that makes the ladder legible. */
const perCredit = (p: Pack) => (p.price_cents / 100 / p.points).toFixed(2);

export function PackCard({ pack, highlight, badge, subtitle, onBuy, buying, locked }: {
  pack: Pack;
  highlight?: boolean;
  badge?: string;
  subtitle: string;
  onBuy: (type: string) => void;
  buying: string | null;
  locked?: boolean;
}) {
  const { t } = useTranslation();
  const isBuying = buying === pack.type;
  return (
    <div
      id={`pack-${pack.type}`}
      data-testid={`pack-${pack.type}`}
      className={`relative flex scroll-mt-24 flex-col rounded-2xl border p-6 ${highlight ? "border-primary shadow-[0_0_40px_hsl(var(--primary)/0.15)]" : "border-border"} bg-card text-card-foreground`}
    >
      {badge && (
        <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-primary-foreground">{badge}</span>
      )}
      <div className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{subtitle}</div>
      <h3 className="text-lg font-bold">{pack.name}</h3>

      {/* Both currencies at the same weight. MXN is what is charged; USD is
          what a US buyer will see on their statement give or take FX. */}
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="whitespace-nowrap">
          <span className="text-3xl font-extrabold" data-testid={`mxn-${pack.type}`}>{mxn(pack.price_cents)}</span>
          <span className="ml-1 text-sm font-bold text-muted-foreground">MXN</span>
        </span>
        <span className="whitespace-nowrap">
          <span className="text-3xl font-extrabold" data-testid={`usd-${pack.type}`}>{usd(pack.usd_cents)}</span>
          <span className="ml-1 text-sm font-bold text-muted-foreground">USD</span>
        </span>
      </div>

      {/* The credit count is the product. It was a text-xs footnote; it is
          the second most important number on the card. */}
      <div className="mt-2 flex items-center gap-2">
        <Coins className="h-4 w-4 text-primary" />
        <span className="text-lg font-bold" data-testid={`credits-${pack.type}`}>
          {t("pricingPage.creditsCount", { credits: pack.points.toLocaleString("en-US") })}
        </span>
      </div>
      <div className="mb-4 mt-1 text-xs text-muted-foreground">
        {pack.kind === "credits"
          ? t("pricingPage.perCredit", { rate: perCredit(pack) })
          : t("pricingPage.oneTime")}
      </div>

      <p className="mb-6 flex-1 text-sm leading-relaxed text-muted-foreground">{pack.description}</p>
      <button
        type="button" data-testid={`buy-${pack.type}`}
        disabled={isBuying || locked}
        onClick={() => onBuy(pack.type)}
        className={`w-full rounded-lg py-3 text-sm font-bold transition-opacity hover:opacity-90 disabled:opacity-60 ${highlight ? "bg-primary text-primary-foreground" : "border border-input text-foreground hover:bg-accent"}`}
      >
        {isBuying ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : t("pricingPage.buyNow")}
      </button>
    </div>
  );
}

/**
 * One pack as a slim row (Studio Blueprint 04, home's "one balance" and the
 * pay sheet): the credit count, both prices, and a Buy button that says what
 * it costs. Same data and the same Buy as PackCard.
 */
export function PackRow({ pack, highlight, badge, note, onBuy, buying, locked }: {
  pack: Pack;
  highlight?: boolean;
  badge?: string;
  /** A short line under the credit count ("Covers this render", "+2 more"). */
  note?: string;
  onBuy: (type: string) => void;
  buying: string | null;
  locked?: boolean;
}) {
  const { t } = useTranslation();
  const isBuying = buying === pack.type;
  return (
    <div
      id={`pack-${pack.type}`}
      data-testid={`pack-${pack.type}`}
      className={`flex scroll-mt-24 items-center gap-3 rounded-2xl border p-3.5 sm:p-4 ${highlight ? "border-primary bg-primary/[0.07]" : "border-border bg-card"}`}
    >
      <Coins className="h-5 w-5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          <span className="font-display text-[1.05rem] font-bold tabular-nums" data-testid={`credits-${pack.type}`}>
            {t("pricingPage.creditsCount", { credits: pack.points.toLocaleString("en-US") })}
          </span>
          {badge && <span className="rounded-full bg-primary px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-primary-foreground">{badge}</span>}
        </div>
        <div className="text-[12.5px] text-muted-foreground">
          {note || (pack.kind === "credits" ? t("pricingPage.perCredit", { rate: perCredit(pack) }) : t("pricingPage.oneTime"))}
        </div>
      </div>
      <button
        type="button"
        data-testid={`buy-${pack.type}`}
        disabled={isBuying || locked}
        onClick={() => onBuy(pack.type)}
        className="flex min-h-[48px] shrink-0 flex-col items-center justify-center rounded-xl bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-3.5 text-[#10131a] transition-transform active:scale-[0.98] disabled:opacity-60"
      >
        {isBuying ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <span className="text-[14px] font-bold leading-tight tabular-nums">
              <span data-testid={`mxn-${pack.type}`}>{mxn(pack.price_cents)}</span> MXN
            </span>
            <span className="text-[11.5px] font-semibold leading-tight tabular-nums opacity-80">
              <span data-testid={`usd-${pack.type}`}>{usd(pack.usd_cents)}</span> USD
            </span>
          </>
        )}
      </button>
    </div>
  );
}
